import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';

// Routes
import { DATA_ACCESS, SUBSCRIPTION_INVOICES } from '@/lib/routes/privateRoutes';

// Components
import H3 from '@/components/base/Headings/H3';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import ChangePlanButton from '@/components/pages/settings/account/UserPlan/ChangePlanButton';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useActiveTreeStructure from '@/utils/hooks/tree-structure/useActiveTreeStructure';

// Lib
import { NO_ACTIVE_PLAN, TRIAL } from '@/lib/plans';
import { SubscriptionType } from '@/types/pricingTypes/subscriptionType';

type RightPanelProps = {
    isAdmin: boolean;
    isManager: boolean;
    plan: SubscriptionType;
};
const RightPanel = ({ isAdmin, isManager, plan }: RightPanelProps) => {
    const { t } = useTranslation('socialprofiles');
    const { isTreeStructureActivated } = useActiveTreeStructure();

    const { user } = useUser({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const { errorAuthDataAccessSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    return (
        <>
            <div>
                <H3 className="mb-m flex items-center">
                    {t('data_access')}
                    {errorAuthDataAccessSocialProfiles?.length > 0 ? (
                        <Icon
                            icon="warning"
                            className="ml-3 text-base text-icoRed"
                            data-testid="right-panel-warning-icon"
                        />
                    ) : null}
                </H3>
                <p className="mb-8 font-GSemiBold text-xs leading-4 text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('click_to_see_data')}
                </p>
                <Link href={DATA_ACCESS}>
                    <>
                        <Button className="w-full" size="md" variant="secondary">
                            {t('go_data_access')}
                        </Button>
                    </>
                </Link>
            </div>
            <hr
                className={clsx('my-8 border-icoGray-300 dark:border-icoDarkMode-beton', {
                    '-mx-10': !isTreeStructureActivated,
                    '-mx-8': isTreeStructureActivated
                })}
            />
            <div>
                <div className="mb-m flex items-center justify-between">
                    <H3>{t('slots_available')}</H3>
                    <span className="ml-1 rounded-full border-2 border-icoBlue px-5 py-2 text-sm text-icoBlue">
                        {user?.quotas?.profiles?.purchased === null
                            ? t('commons:unlimited')
                            : `${user?.quotas?.profiles?.remaining}/${user?.quotas?.profiles?.purchased}`}
                    </span>
                </div>

                {isManager ? (
                    <p className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {t('contact_admin_for_more_slots')}
                    </p>
                ) : null}
                {isAdmin ? (
                    <>
                        {plan.product_key !== TRIAL && plan.product_key !== NO_ACTIVE_PLAN ? (
                            <>
                                <p className="mb-5 font-GMedium text-xs leading-4 text-icoGray-500 dark:text-icoDarkMode-wolf">
                                    {t('upgrade_plan_notice')}
                                </p>
                                <ChangePlanButton
                                    plan={plan}
                                    size="md"
                                    variant="secondary"
                                    icon="arrow-top"
                                />
                            </>
                        ) : null}
                        <Link
                            href={SUBSCRIPTION_INVOICES}
                            passHref
                            className="group mt-5 block cursor-pointer space-y-1 rounded-md bg-icoGray-100 p-5 dark:bg-icoDarkMode-deepBlack">
                            <span className="mb-2 text-xxs leading-none tracking-0.5 text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {t('your_plan')}
                            </span>
                            <span className="flex items-center text-sm">
                                {t(
                                    `commons:pricing.plans.${plan.product_key}`,
                                    {},
                                    { fallback: `commons:subscriptions.${plan.product_title}` }
                                )}
                                <Icon
                                    icon="chevron-right"
                                    className="ml-3 text-icoGray-500 transition ease-in-out group-hover:translate-x-1 dark:text-icoDarkMode-wolf"
                                />
                            </span>
                        </Link>
                    </>
                ) : null}
            </div>
        </>
    );
};

export default RightPanel;
